import { Box, Stack, Typography } from '@mui/material';
import { numericSx } from '../../../theme/typography';
import { avatarBarIndex } from '../../../utils/avatarHash';
import { CompanyAvatar } from '../../shared/CompanyAvatar';
import { PanelCard } from './PanelCard';
import type { DashboardCompany } from '../types';

export interface UpcomingAllocationRow {
  id: string;
  projectId: string;
  projectName: string;
  clientName?: string | null;
  /** The project's company (branding) — renders a `CompanyAvatar` (Phase 8). */
  company?: DashboardCompany | null;
  /** "Starts 02 Jun" — pre-formatted by the caller. */
  startsLabel: string;
  /** 0–100 — render as "60%" pill. */
  percentage: number;
}

interface UpcomingAllocationsListProps {
  rows: UpcomingAllocationRow[];
  loading?: boolean;
}

/**
 * "Upcoming allocations" row-list for the Allocations section — exact
 * prototype port of `UpcomingAlloc` (`dashboard-v2.jsx` lines 352-371).
 *
 * Same shape as `MyProjects` but the right slot is a pill showing the
 * allocation percentage in `accent.tint` + `primary.main`.
 *
 * Rows show **future** allocations (start > today); the AllocationsSection
 * slices the `myAllocations` payload accordingly.
 */
export function UpcomingAllocationsList({
  rows,
  loading,
}: UpcomingAllocationsListProps) {
  return (
    <PanelCard title="Upcoming allocations" data-testid="widget-upcoming-allocations">
      {rows.length === 0 ? (
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', py: 2 }}>
          {loading ? 'Loading…' : 'No upcoming allocations.'}
        </Typography>
      ) : (
        <Stack spacing="10px">
          {rows.map((row) => (
            <UpcomingRow key={row.id} row={row} />
          ))}
        </Stack>
      )}
    </PanelCard>
  );
}

function UpcomingRow({ row }: { row: UpcomingAllocationRow }) {
  const barIdx = avatarBarIndex(row.projectName);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {row.company ? (
        <CompanyAvatar
          name={row.company.name}
          logoId={row.company.logoId}
          icon={row.company.icon}
          color={row.company.color}
          size={24}
          aria-hidden
        />
      ) : (
        <Box
          sx={(theme) => ({
            width: 3,
            alignSelf: 'stretch',
            minHeight: 32,
            backgroundColor: theme.palette.bars[barIdx].fill,
            borderRadius: '2px',
            flexShrink: 0,
          })}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.78125rem',
            fontWeight: 600,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.3,
          }}
        >
          {row.projectName}
        </Typography>
        <Typography
          sx={[
            numericSx,
            {
              fontSize: '0.6875rem',
              color: 'text.disabled',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          ]}
        >
          {row.clientName ? `${row.clientName} · ` : ''}
          {row.startsLabel}
        </Typography>
      </Box>
      <Box
        sx={[
          numericSx,
          (theme) => ({
            backgroundColor: theme.palette.accent.tint,
            color: theme.palette.primary.main,
            fontSize: '0.6875rem',
            fontWeight: 600,
            px: '8px',
            py: '3px',
            borderRadius: 999,
            flexShrink: 0,
          }),
        ]}
      >
        {row.percentage}%
      </Box>
    </Box>
  );
}
